/**
 * The monitoring timeline's CSV export: one row per route, and for each stop a block of columns
 * for planned and actual arrival and departure, so one day can be compared against another.
 */
import { format } from "date-fns"

import { getDisplayRouteDelayMinutes } from "@/lib/routeDelayDisplay"
import { getStopTimeVariance } from "@/lib/statusDisplay"
import type { Route } from "@/types/collections"

const escapeCSV = (value: unknown): string => {
  if (value === null || value === undefined) return ""
  const str = String(value)
  if (str.includes(",") || str.includes('"') || str.includes("\n")) {
    return `"${str.replace(/"/g, '""')}"`
  }
  return str
}

const fmt = (d: Date | undefined) => (d ? format(d, "yyyy-MM-dd HH:mm") : "")

export function buildMonitoringRoutesCsv(routes: Route[], dateStr: string): string {
  const maxStops = Math.max(...routes.map((r) => r.stops.length), 1)
  const routeHeaders = [
    "Date",
    "Route Code",
    "Route Status",
    "Driver",
    "Vehicle",
    "Depot",
    "Planned Route Start",
    "Planned Route End",
    "Actual Route Start",
    "Actual Route End",
    "Route Delay (min)",
    "Total Stops",
    "Completed Stops",
  ]
  const stopHeaderBlocks: string[] = []
  for (let i = 1; i <= maxStops; i++) {
    stopHeaderBlocks.push(
      `Stop ${i} Name`,
      `Stop ${i} Planned Arrival`,
      `Stop ${i} Planned Departure`,
      `Stop ${i} Actual Arrival`,
      `Stop ${i} Actual Departure`,
      `Stop ${i} Arrival Delay (min)`,
      `Stop ${i} Departure Delay (min)`
    )
  }
  const headers = [...routeHeaders, ...stopHeaderBlocks]

  const rows: string[][] = []

  routes.forEach((route) => {
    const routeDelay = getDisplayRouteDelayMinutes(route) ?? 0
    const routeCells = [
      dateStr,
      route.code,
      route.status,
      route.driverName || "",
      route.vehicleReg || "",
      route.depot || "",
      fmt(route.plannedStartTime),
      fmt(route.plannedEndTime),
      fmt(route.actualStartTime),
      fmt(route.actualEndTime),
      String(routeDelay),
      String(route.totalStops),
      String(route.completedStops),
    ]

    const stopCells: string[] = []
    for (let i = 0; i < maxStops; i++) {
      const stop = route.stops[i]
      if (!stop) {
        stopCells.push("", "", "", "", "", "", "")
        continue
      }
      // Same definition of drift the stop sheet prints, so an exported figure and the panel
      // an operator checked it against never differ by a minute of rounding.
      const arrivalDelay =
        getStopTimeVariance(stop.plannedArrival, stop.actualArrival)?.minutes ?? ""
      const departureDelay =
        getStopTimeVariance(stop.plannedDeparture, stop.actualDeparture)?.minutes ?? ""
      stopCells.push(
        stop.name,
        fmt(stop.plannedArrival),
        fmt(stop.plannedDeparture),
        fmt(stop.actualArrival),
        fmt(stop.actualDeparture),
        String(arrivalDelay),
        String(departureDelay)
      )
    }

    rows.push([...routeCells, ...stopCells])
  })

  return [
    headers.map(escapeCSV).join(","),
    ...rows.map((row) => row.map(escapeCSV).join(",")),
  ].join("\n")
}
